import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Typography, Box } from "@mui/material";
import toast from "react-hot-toast";

import { PublicShareContainer } from "./components/PublicShareContainer";
import { ProjectPublicPanel } from "./components/ProjectPublicPanel";
import { FeedbackCriteriaDialog } from "../../videoProduction/components/videoReview/FeedbackCriteriaDialog";
import { BRAND_PROJECT_STATUSES } from "../../videoProduction/helper";
import { PublicProject } from "./OrderPublicPage";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import { getBrandAllowedFileTypes } from "../../admin/videos/selectedVideoCardV2/helpers";
import axiosInstant from "../../../api";
import { getBrandProject, getPublicProjectPage } from "../../../api/endpoints";

type ProjectShareData = PublicProject & {
  brand?: {
    id: number;
    name: string;
    logo?: string;
    rmn?: boolean;
    enabled_file_types?: string[];
    pre_approval_downloading?: boolean;
  } | null;
};

export default function ProjectSharePage() {
  const { id, token } = useParams<{ id?: string; token?: string }>();

  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectShareData | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const fetchByToken = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await axiosInstant.get(getPublicProjectPage(token));
      setProject(response.data);
    } catch (error: any) {
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        toast.error("Failed to load project");
      }
      navigate("/404");
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    const fetchById = async () => {
      try {
        setLoading(true);
        const response = await axiosInstant.get(getBrandProject(Number(id)));
        if (response.data?.public_page_token) {
          navigate(`/projects/public/${response.data.public_page_token}`, {
            replace: true,
          });
        } else {
          setUnavailable(true);
        }
      } catch (error: any) {
        if (error.response?.status === 403 || error.response?.status === 404) {
          navigate(`/video-production/projects/${id}`);
        } else {
          toast.error("Failed to load project");
          navigate("/404");
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchByToken();
    } else if (id) {
      fetchById();
    }
  }, [id, token, navigate, fetchByToken]);

  if (loading) {
    return (
      <PublicShareContainer title="Loading...">
        <Box className="flex justify-center items-center min-h-[400px]">
          <DotLottleLoader />
        </Box>
      </PublicShareContainer>
    );
  }

  if (unavailable) {
    return (
      <PublicShareContainer title="This link isn't available">
        <Box className="flex flex-col items-center justify-center gap-2 min-h-[300px] text-center">
          <Typography variant="body1" color="dark1">
            This project hasn&apos;t been shared yet.
          </Typography>
          <Typography variant="body2" color="darkgray1">
            Reach out to your Desire Company contact to have its shareable page
            turned on.
          </Typography>
        </Box>
      </PublicShareContainer>
    );
  }

  if (!project) {
    return null;
  }

  // Token-based public page: viewers are external, so brand file-type
  // settings always apply (matches OrderPublicPage).
  const allowedFileTypes = getBrandAllowedFileTypes([project.brand]);

  const descriptionContent = (
    <>
      {project.brand && (
        <Typography variant="body2" color="darkgray1">
          Brand: {project.brand.name}
        </Typography>
      )}
      {project.description && (
        <Typography variant="body2" color="darkgray1">
          {project.description}
        </Typography>
      )}
    </>
  );

  return (
    <PublicShareContainer title={project.title} descriptionContent={descriptionContent}>
      <ProjectPublicPanel
        project={project}
        token={token as string}
        onRefresh={fetchByToken}
        defaultExpanded
        publicBase="brand-projects"
        allowedFileTypes={allowedFileTypes}
      />
      <FeedbackCriteriaDialog
        autoShow={project.status === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED}
        seenKey={`feedbackCriteriaSeen_project_${project.id}`}
      />
    </PublicShareContainer>
  );
}
